// WebSocket event types
export const WS_EVENTS = {
    // Connection
    CONNECT: 'connect',
    DISCONNECT: 'disconnect',
    ERROR: 'error',
    // Chat
    MESSAGE_SEND: 'message:send',
    MESSAGE_RECEIVED: 'message:received',
    // D206: the same payload, to the sender's own sockets (all their devices,
    // the sending one too). Never unread, never a sound.
    MESSAGE_SENT: 'message:sent',
    MESSAGE_READ: 'message:read',
    MESSAGE_TYPING: 'message:typing',
    // M20: an edit or delete (full message payload) and reaction changes.
    MESSAGE_UPDATED: 'message:updated',
    MESSAGE_REACTION: 'message:reaction',
    // M60 (D220): a poll's votes changed or it closed, with the new counts.
    MESSAGE_POLL: 'message:poll',
    // M71 (D282): a gathering's replies, arrivals or cancellation; and the
    // "Come now" ring, to the people not yet at the table (D286).
    MESSAGE_GATHERING: 'message:gathering',
    GATHERING_RING: 'gathering:ring',
    // M63: my own settings for a chat changed (archive, unread mark, mute;
    // D231–D233), to my sockets only; a group's info changed (D234), to
    // everyone in it.
    CONVERSATION_SETTINGS: 'conversation:settings',
    CONVERSATION_UPDATED: 'conversation:updated',
    // #97: who is in a group changed (made, joined, left, removed, a role,
    // deleted): `{ conversationId, familyId }` to everyone it touched, the
    // people who left too. Clients fetch the chat again; a 404 drops it.
    CONVERSATION_MEMBERS: 'conversation:members',
    // M64 (docs/chat-round-six.md): disappearing messages that expired were
    // deleted (D238), to everyone in the chat; my scheduled messages changed
    // (D239), to my sockets only.
    MESSAGE_DELETED: 'message:deleted',
    MESSAGE_SCHEDULED: 'message:scheduled',
    // Presence
    USER_ONLINE: 'user:online',
    USER_OFFLINE: 'user:offline',
    // The health canary's round trip to its own server (M67, D253); no token.
    HEALTH_PING: 'health:ping',
    HEALTH_PONG: 'health:pong',
    USER_TYPING: 'user:typing',
    // Live voice/video calls (M19). Same names are used client→server (intent)
    // and server→client (relay); the payload shape differs by direction.
    CALL_INITIATE: 'call:initiate',
    CALL_ACCEPT: 'call:accept',
    CALL_REJECT: 'call:reject',
    CALL_END: 'call:end',
    CALL_REJOIN: 'call:rejoin',
    CALL_SIGNAL: 'call:signal',
    CALL_ERROR: 'call:error',
    // M24 (docs/calls-round-two.md)
    CALL_MEDIA: 'call:media',
    CALL_RING: 'call:ring',
    CALL_PARTICIPANT: 'call:participant',
    // D278 (docs/calls-round-two.md §8): a callee's phone is ringing.
    CALL_RINGING: 'call:ringing',
    // M28: a device appeared or someone joined a chat; holders share keys.
    KEYS_NEEDED: 'keys:needed',
    // M28: a device was given a chat key; it can open those messages now.
    KEYS_SHARED: 'keys:shared',
    // Client → server: whether the app is in front, and which chat it shows.
    // The server skips notifications people are already looking at.
    VIEWING: 'presence:viewing',
    // Family
    FAMILY_MEMBER_JOINED: 'family:member:joined',
    FAMILY_MEMBER_LEFT: 'family:member:left',
    FAMILY_UPDATED: 'family:updated',
    // Someone changed their name or profile photo (#71): everyone who shares a
    // family with them, and their own other devices, show it straight away.
    USER_UPDATED: 'user:updated',
    // A root deleted the family (M54, D189).
    FAMILY_DELETED: 'family:deleted',
    FAMILY_INVITE_RECEIVED: 'family:invite:received',
    // M48: family link requests and link changes (D152–D156).
    JOIN_REQUEST_CREATED: 'family:join-request:created',
    JOIN_REQUEST_DECIDED: 'family:join-request:decided',
    JOIN_LINK_CHANGED: 'family:join-link:changed',
    // M58: a member was reported (to the family's roots, D207); the blocker's
    // own devices hear about block changes (D208).
    REPORT_CREATED: 'family:report:created',
    BLOCKS_CHANGED: 'user:blocks:changed',
    // Family apps (family-apps spec §7). Stars deliberately emit nothing (D14).
    LINK_ADDED: 'link:added',
    LINK_REMOVED: 'link:removed',
    DOCUMENT_ADDED: 'document:added',
    DOCUMENT_REMOVED: 'document:removed',
    // M26: folder, tags or note changed.
    DOCUMENT_UPDATED: 'document:updated',
    LIST_CREATED: 'list:created',
    LIST_UPDATED: 'list:updated',
    LIST_DELETED: 'list:deleted',
    LIST_ITEM_ADDED: 'list:item:added',
    LIST_ITEM_UPDATED: 'list:item:updated',
    LIST_ITEM_DELETED: 'list:item:deleted',
};
// Chat essentials (M20, docs/chat-essentials.md)
export const MESSAGE_LIMITS = {
    EDIT_WINDOW_MINUTES: 15, // D22
    QUICK_REACTIONS: ['👍', '❤️', '😂', '😮', '😢', '🙏'], // D21
    REPLY_PREVIEW_LENGTH: 140,
    SEARCH_MIN_LENGTH: 2, // D26
    SEARCH_MAX_LENGTH: 100,
    SEARCH_RESULTS: 30,
    JUMP_WINDOW: 500, // messages loaded when jumping to a search hit
    TYPING_SEND_INTERVAL_MS: 3000, // D24
    TYPING_EXPIRE_MS: 6000,
    MAX_PINS: 3, // M22, D28
    FORWARD_MAX_TARGETS: 5, // M22, D29
};
// Chat round five (M63, docs/chat-round-five.md)
export const CHAT_LIMITS = {
    DESCRIPTION_MAX: 300, // D234, characters before sealing
    SEALED_DESCRIPTION_MAX: 2400, // ciphertext characters the server accepts
    PRIVATE_QUOTE_LENGTH: 120, // D235
};
// Chat round six (M64, docs/chat-round-six.md)
export const CHAT_PIN_LIMIT = 3; // D237, per person per family
/** Disappearing messages (D238): how long a new message lasts. */
export const DISAPPEARING_SECONDS = {
    '24h': 24 * 60 * 60,
    '7d': 7 * 24 * 60 * 60,
    '90d': 90 * 24 * 60 * 60,
};
/** Scheduled messages (D239). */
export const SCHEDULE_LIMITS = {
    MIN_LEAD_MS: 5 * 60 * 1000,
    MAX_LEAD_MS: 30 * 24 * 60 * 60 * 1000,
    /** Allowance for the trip to the server and a slow clock. */
    GRACE_MS: 60 * 1000,
    MAX_PER_CHAT: 25,
    MAX_PER_PERSON: 100,
    /** Characters of (sealed) text the server keeps for one. */
    CONTENT_MAX: 100_000,
};
/** How long "Mute" lasts (D233); Always has no end time. */
export const MUTE_DURATIONS_MS = {
    '8h': 8 * 60 * 60 * 1000,
    '1w': 7 * 24 * 60 * 60 * 1000,
};
// Message types
export const MESSAGE_TYPES = {
    TEXT: 'text',
    IMAGE: 'image',
    GIF: 'gif',
    VIDEO: 'video',
    FILE: 'file',
    VOICE: 'voice',
    SYSTEM: 'system',
    // M60 (docs/chat-round-three.md): always end-to-end encrypted.
    ALBUM: 'album', // D219
    POLL: 'poll', // D220
    GATHERING: 'gathering', // D282, M71: always sealed
};
// Photo albums (M60, D219): 2-10 photos or videos as one message. Each item
// lists its file and maybe a preview, so up to 20 files.
export const ALBUM_LIMITS = {
    MIN_ITEMS: 2,
    MAX_ITEMS: 10,
    MAX_FILES: 20,
};
// Polls (M60, D220).
export const POLL_LIMITS = {
    QUESTION_LENGTH: 200,
    OPTION_LENGTH: 80,
    MIN_OPTIONS: 2,
    MAX_OPTIONS: 10,
    // The furthest ahead a close time may be (the composer offers 1h/1d/1w).
    MAX_OPEN_MS: 7 * 24 * 60 * 60 * 1000,
    // An absolute close time this far in the past still sends: it closes
    // when it arrives (a poll that waited offline).
    PAST_GRACE_MS: 5 * 60 * 1000,
};
// Gatherings, "Dinner call" (M71, D282–D289).
export const GATHERING_LIMITS = {
    TITLE_LENGTH: 80,
    PLACE_LENGTH: 120,
    NOTE_LENGTH: 300,
    // The furthest ahead a gathering may be set.
    MAX_AHEAD_MS: 60 * 24 * 60 * 60 * 1000,
    // A start this far in the past still sends (a gathering that waited offline).
    PAST_GRACE_MS: 5 * 60 * 1000,
    REMINDER_BEFORE_MS: 10 * 60 * 1000,
    // It stays open to join and ring this long after the start (D286).
    OPEN_AFTER_START_MS: 3 * 60 * 60 * 1000,
    MAX_RINGS: 2,
    RING_AGAIN_AFTER_MS: 3 * 60 * 1000,
    SNOOZE_MS: 5 * 60 * 1000,
    LATE_MINUTES: [15, 30, 60],
    RING_DEFAULT_MAX_PEOPLE: 8,
};
// User kinds (identity spec §3.1 — 'app' reserved for future app accounts)
export const USER_KINDS = {
    HUMAN: 'human',
    APP: 'app',
};
// Conversation types
export const CONVERSATION_TYPES = {
    DIRECT: 'direct',
    GROUP: 'group',
};
// Group-level participant roles (D11): family roots/admins override these
export const CONVERSATION_ROLES = {
    ADMIN: 'admin',
    MEMBER: 'member',
};
// File upload limits
export const FILE_LIMITS = {
    MAX_SIZE: 50 * 1024 * 1024, // 50MB
    MAX_VOICE_DURATION: 300, // seconds (5 min)
    // Voice-note waveform: bar heights 0-100 captured while recording (#1).
    VOICE_WAVEFORM_BARS: 48,
    ALLOWED_IMAGE_TYPES: [
        'image/jpeg',
        'image/png',
        'image/gif',
        'image/webp',
        'image/avif',
    ],
    ALLOWED_VIDEO_TYPES: ['video/mp4', 'video/webm', 'video/quicktime'],
    ALLOWED_AUDIO_TYPES: [
        'audio/webm',
        'audio/ogg',
        'audio/mpeg',
        'audio/mp4',
        'audio/wav',
        'audio/x-wav',
    ],
    ALLOWED_FILE_TYPES: [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain',
    ],
};
// Family roles (hierarchical: root > admin > member)
export const FAMILY_ROLES = {
    ROOT: 'root',
    ADMIN: 'admin',
    MEMBER: 'member',
};
// Enrollment tokens (one-time QR registration/add-device secrets)
export const ENROLLMENT_TOKEN_KINDS = {
    ENROLL: 'enroll',
    ADD_DEVICE: 'add_device',
};
export const ENROLLMENT_TOKEN_STATUS = {
    PENDING: 'pending',
    CONSUMED: 'consumed',
    REVOKED: 'revoked',
    EXPIRED: 'expired',
};
// Family invites (existing user → additional family, accept/decline in-app)
export const FAMILY_INVITE_STATUS = {
    PENDING: 'pending',
    ACCEPTED: 'accepted',
    DECLINED: 'declined',
    REVOKED: 'revoked',
    EXPIRED: 'expired',
};
// Family limits
export const FAMILY_LIMITS = {
    MAX_NAME_LENGTH: 100,
    MAX_DESCRIPTION_LENGTH: 500,
    INVITATION_EXPIRY_DAYS: 7,
    // Pictures are resized to 512 px on the device first (tens of KB). The
    // cap stays well under the server's 1 MB request limit, so a big picture
    // gets a clear INVALID_IMAGE instead of a bare 413 (issue #24).
    MAX_AVATAR_SIZE: 512 * 1024,
    /** The same cap as base64 text, plus room for the data: prefix. */
    MAX_AVATAR_BASE64_LENGTH: 900 * 1024,
    /** Sealed picture text (D293): a 512 px JPEG sealed and base64'd, with room. */
    MAX_SEALED_PICTURE_LENGTH: 400_000,
    MAX_HANDLE_LENGTH: 30,
    MIN_HANDLE_LENGTH: 3,
};
/**
 * The day pictures became sealed (D297). Readable pictures left over are
 * swept 14 days later.
 */
export const PICTURE_CUTOVER = '2026-10-29';
// Family apps (family-apps spec §3–§6)
export const FAMILY_APP_LIMITS = {
    LINK_NOTE_LENGTH: 500,
    DOCUMENT_DESCRIPTION_LENGTH: 500,
    LIST_NAME_LENGTH: 100,
    LIST_ITEM_TEXT_LENGTH: 300,
    LIST_ITEM_NOTE_LENGTH: 500,
    PAGE_SIZE: 50,
    // M26 (docs/family-apps-round-two.md)
    DOCUMENT_FOLDER_LENGTH: 60,
    DOCUMENT_TAGS: 10,
    DOCUMENT_TAG_LENGTH: 30,
    LIST_TEMPLATES: 50,
};
/** How often a list item comes back (M26, D47). */
export const LIST_REPEATS = ['daily', 'weekly', 'monthly'];
// Link preview cache (family-apps spec §4.1)
export const LINK_PREVIEW_LIMITS = {
    FAILED_CACHE_MS: 24 * 60 * 60 * 1000, // failed fetches are retried after 24h
    MAX_BYTES: 1024 * 1024, // ≤1MB
    TIMEOUT_MS: 5000,
    MAX_REDIRECTS: 3,
    // The preview's picture (D318–D319): fetched by the server for the
    // sender's device only, never kept, never fetched by a reader's device.
    IMAGE_MAX_BYTES: 2 * 1024 * 1024, // the site's picture, as fetched
    IMAGE_TIMEOUT_MS: 4000, // the picture's whole fetch, redirects included
    IMAGE_TYPES: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'],
    // The sealed copy, made on the sender's device.
    SEALED_IMAGE_MAX_SIDE: 400,
    SEALED_IMAGE_MAX_BYTES: 32 * 1024,
    SEALED_IMAGE_QUALITIES: [0.7, 0.5],
    // How long a sending device waits for the preview, picture included.
    SEND_WAIT_MS: 9000,
};
// Live voice/video calls (M19). Mesh WebRTC is fine at family scale.
export const CALL_LIMITS = {
    // Mesh cost grows quadratically; cap the room so a family call stays usable.
    MAX_PARTICIPANTS: 6,
    RING_TIMEOUT_MS: 45_000,
    // TURN credentials minted from a shared secret are valid for this long.
    TURN_CREDENTIAL_TTL_SECONDS: 60 * 60,
};
export const CALL_TYPES = {
    VOICE: 'voice',
    VIDEO: 'video',
};
// Web Push notifications (M17)
export const PUSH_LIMITS = {
    MAX_SUBSCRIPTIONS_PER_USER: 10,
    MAX_MUTED_FAMILIES: 50,
    MAX_MUTED_CONVERSATIONS: 200,
    BODY_LENGTH: 140,
    TTL_SECONDS: 60 * 60, // 1h — stale notifications are dropped by the push service
};
// Enrollment token limits (identity spec §4.8)
export const ENROLLMENT_LIMITS = {
    DEFAULT_EXPIRY_DAYS: 7,
    MAX_EXPIRY_DAYS: 30,
    TOKEN_BYTES: 32, // raw token = 32 CSPRNG bytes, hex-encoded to 64 chars
};
// App review access (M56, D202–D205, docs/app-review-access.md)
export const REVIEW_LIMITS = {
    LINK_DAYS: 30, // a review link's life; its sessions end with it (D203, D205)
    TOKEN_BYTES: 32, // hex-encoded to 64 chars, only the hash is stored
    OPENS_PER_MINUTE: 10, // per IP, like device links
    MAX_MEMBERS: 8, // demo members in the review family
    FAMILY_NAME: 'Twinden Review Family',
};
// Sign in by scanning (M49, D157–D162, docs/qr-sign-in.md)
export const QR_SIGN_IN_LIMITS = {
    CODE_SECONDS: 60, // a code's life (D157)
    MAX_REFRESHES: 10, // the computer replaces it this often, then "Show a new code"
    POLL_INTERVAL_MS: 2000, // the computer asks whether it was approved
    SECRET_BYTES: 32, // public code and private poll secret, base64url
    NUMBER_CHOICES: 3, // numbers offered on the phone, one right (D159)
    CREATES_PER_HOUR: 30, // per IP (D162)
    POLLS_PER_MINUTE: 60, // per IP
    APPROVALS_PER_MINUTE: 10, // per account
};
// What kind of device the computer says it is (D159): a fixed list the phone
// names in its own words, never text from the server.
export const QR_DEVICE_KINDS = [
    'iphone',
    'ipad',
    'android',
    'mac',
    'windows',
    'linux',
    'other',
];
// How a device opened Twinden (M53, D186): the installed web app, a browser
// tab, or a native iOS/Android app. Only this is stored about the device.
export const APP_MODES = ['installed', 'browser', 'native'];
// The admin count looks back this far, and an installed/native report this
// recent isn't overwritten by a browser one (D186).
export const APP_MODE_WINDOW_DAYS = 30;
// Admin panel lists (#73): 50 a page with "Load more", at most 200 a request;
// searches wait 300 ms after the last keystroke.
export const ADMIN_PAGE = {
    SIZE: 50,
    MAX: 200,
    SEARCH_DEBOUNCE_MS: 300,
};
// Signed-in sessions (D212, docs/adding-devices.md). How a session signed in:
// a passkey on this device, a passkey on another device (the browser's "use
// a phone"), a phone scanning this computer's code (M49), making a passkey
// here (enrollment, join links), or a review link (M56).
export const SESSION_METHODS = [
    'passkey',
    'passkey_other',
    'qr',
    'enrolled',
    'review',
];
// What a session is, parsed from its user agent on the server (D212). A
// fixed list the app names in its own words.
export const SESSION_OSES = [
    'iphone',
    'ipad',
    'android',
    'mac',
    'windows',
    'linux',
    'chromeos',
];
export const SESSION_BROWSERS = [
    'chrome',
    'safari',
    'firefox',
    'edge',
    'samsung',
    'opera',
];
// The native apps say which they are in `X-Twinden-Client: ios/1.4.0` (D212).
export const SESSION_APPS = ['ios', 'android'];
export const TWINDEN_CLIENT_HEADER = 'x-twinden-client';
// last_used_at is written at most this often per session (D212).
export const SESSION_TOUCH_INTERVAL_MS = 60 * 60 * 1000;
// The sign-in log (M65, docs/lawful-requests.md, D242): what each row is,
// and how long rows and deleted-account records are kept (D243, D245) —
// 180 days, as the CERT-In directions (2022) and the IT Rules 2021 require.
// Seen only by the system root; never shown in the app.
export const SIGN_IN_EVENT_KINDS = [
    'sign_in',
    'qr_approve',
    'sign_out',
    'refresh_rejected',
    'review_link',
];
export const SIGN_IN_LOG_RETENTION_DAYS = 180;
// Family link limits (D156)
export const JOIN_LIMITS = {
    MAX_PENDING_PER_FAMILY: 100,
    DECLINE_COOLDOWN_DAYS: 7,
    ABANDONED_ACCOUNT_DAYS: 30,
    NOTE_MAX: 200,
};
// Shape Twinden (M55, docs/run-by-families.md D198–D200).
export const IDEA_LIMITS = {
    TITLE_MAX: 80,
    BODY_MAX: 600,
    NOTE_MAX: 300,
    /** A poster may edit their own idea this long after posting (D200). */
    EDIT_MINUTES: 15,
    /** Posts per person per rolling 24 hours (D200). */
    PER_DAY: 3,
    /** The public board's size: the most wanted live ideas… */
    BOARD_ACTIVE: 200,
    /** …and the latest done and the latest not-now ideas, each (D199). */
    BOARD_FOLDED: 50,
};
// Report, block and contact (M58, docs/report-block-contact.md D207–D208).
export const REPORT_REASONS = [
    'harassment',
    'spam',
    'impersonation',
    'other',
];
export const REPORT_LIMITS = {
    NOTE_MAX: 500,
    /** Reports per person per rolling 24 hours (D207). */
    PER_DAY: 5,
    /** The admin list's size (D210). */
    ADMIN_LIST_MAX: 500,
};
export const IDEA_STATUSES = [
    'open',
    'planned',
    'building',
    'done',
    'not_now',
];
// Handles that can never be assigned (identity spec §2)
export const RESERVED_HANDLES = [
    'root',
    'admin',
    'system',
    'support',
    'twinden',
    'twinden',
    'api',
    'www',
    'help',
    // Mention keywords (M25, D43)
    'everyone',
    'here',
];
/**
 * Family handles (M59, D215): a separate namespace from user handles, so the
 * user list is reserved too, plus a few words that would read as Twinden's.
 */
export const RESERVED_FAMILY_HANDLES = [
    ...RESERVED_HANDLES,
    'family',
    'families',
    'join',
    'new',
    'settings',
    'invite',
    'invites',
    'official',
    'staff',
    'team',
];
export const FAMILY_HANDLE_LIMITS = {
    MIN_LENGTH: 3,
    MAX_LENGTH: 32,
    /** Roots may change it at most this often (D216). */
    MAX_CHANGES: 3,
    CHANGE_WINDOW_DAYS: 30,
};
/** `@everyone` in a group chat mentions the whole chat (M25, D43). */
export const EVERYONE_MENTION = 'everyone';
// Suggestions for what you call a family member (D143), English and Hindi.
export const RELATION_SUGGESTIONS = [
    ['Maa', 'माँ'],
    ['Papa', 'पापा'],
    ['Dadi', 'दादी'],
    ['Dadu', 'दादू'],
    ['Nani', 'नानी'],
    ['Nanu', 'नानू'],
    ['Bua', 'बुआ'],
    ['Phupha', 'फूफा'],
    ['Chacha', 'चाचा'],
    ['Chachi', 'चाची'],
    ['Mama', 'मामा'],
    ['Mami', 'मामी'],
    ['Mausi', 'मौसी'],
    ['Mausa', 'मौसा'],
    ['Tau', 'ताऊ'],
    ['Tai', 'ताई'],
    ['Bhaiya', 'भैया'],
    ['Didi', 'दीदी'],
    ['Bhabhi', 'भाभी'],
    ['Jiju', 'जीजू'],
    ['Beta', 'बेटा'],
    ['Beti', 'बेटी'],
    ['Biwi', 'बीवी'],
    ['Pati', 'पति'],
    ['Patni', 'पत्नी'],
    ['Saas', 'सास'],
    ['Sasur', 'ससुर'],
    ['Devar', 'देवर'],
    ['Devrani', 'देवरानी'],
    ['Jeth', 'जेठ'],
    ['Jethani', 'जेठानी'],
    ['Nanad', 'ननद'],
    ['Saala', 'साला'],
    ['Saali', 'साली'],
    ['Bahu', 'बहू'],
    ['Damad', 'दामाद'],
    ['Bhai', 'भाई'],
    ['Behen', 'बहन'],
    ['Bhatija', 'भतीजा'],
    ['Bhatiji', 'भतीजी'],
    ['Bhanja', 'भांजा'],
    ['Bhanji', 'भांजी'],
    ['Pota', 'पोता'],
    ['Poti', 'पोती'],
    ['Nati', 'नाती'],
    ['Natin', 'नातिन'],
];
// Post-quantum key wrapping (M78, docs/post-quantum.md D335–D340).
export const PQ = {
    /** ML-KEM-768 encapsulation key (FIPS 203 `ek`), bytes. */
    MLKEM_PUBLIC_KEY_BYTES: 1184,
    /** ML-KEM-768 ciphertext, bytes. */
    MLKEM_CIPHERTEXT_BYTES: 1088,
    /** A v1 wrapped key: iv[12] ‖ AES-GCM(raw key[32]) ‖ tag[16]. */
    WRAPPED_V1_BYTES: 60,
    /** A v2 wrapped key: 02 ‖ ct[1088] ‖ iv[12] ‖ body[48] (D337). */
    WRAPPED_V2_BYTES: 1149,
};
/**
 * When devices without an ML-KEM key stop getting new chat and family keys
 * (D339, docs/post-quantum.md §6.5): launch (6 Nov 2026) plus 60 days, as
 * M78 ships at launch (owner, 2026-10-30). Apps treat the grace as over when this date has
 * passed or a keys answer says `pqGraceOver: true`; the server's own date
 * is `PQ_GRACE_ENDS_AT` in its environment, defaulting to this.
 */
export const PQ_GRACE_ENDS_AT = '2027-01-05T00:00:00Z';
/** Is the grace over by this device's own date (D339)? */
export function pqGraceOverByDate(now = Date.now()) {
    return now >= Date.parse(PQ_GRACE_ENDS_AT);
}
/**
 * A wrapped copy's format, told by its length and first byte (D337):
 * 60 bytes is v1, 1149 bytes starting 02 is v2, anything else is refused.
 * Works on the base64 text, without decoding.
 */
export function wrappedKeyFormat(base64) {
    if (base64.length % 4 !== 0 || !/^[A-Za-z0-9+/]*={0,2}$/.test(base64)) {
        return 'invalid';
    }
    const pad = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
    const bytes = (base64.length / 4) * 3 - pad;
    if (bytes === PQ.WRAPPED_V1_BYTES)
        return 'v1';
    // The first byte is the first character's six bits and the second's top
    // two: 0x02 is "A" followed by one of "g".."v".
    if (bytes === PQ.WRAPPED_V2_BYTES &&
        base64[0] === 'A' &&
        /^[g-v]$/.test(base64[1] ?? '')) {
        return 'v2';
    }
    return 'invalid';
}
//# sourceMappingURL=constants.js.map